import React from 'react'

export const Button = React.forwardRef<
  HTMLButtonElement,
  {
    children: React.ReactNode
    onClick?: () => void
    disabled?: boolean
    variant?: 'outline' | 'primary'
    size: 'sm' | 'md' | 'lg'
    color?: 'primary' | 'secondary' | 'tertiary' | 'info' | 'success' | 'warning' | 'danger'
    icon?: React.ReactNode
    tooltip?: string
    trigger?: boolean
    title?: string
    grow?: boolean
    href?: string
    target?: string
  }
>(
  (
    {
      children,
      onClick,
      disabled = false,
      icon,
      tooltip,
      trigger = false,
      variant = 'primary',
      size,
      color = 'primary',
      title,
      grow = false,
      href,
      target,
    },
    ref
  ) => {
    const Element = href ? 'a' : trigger ? 'div' : 'button'

    const colorMap = {
      outline: {
        primary: 'border-primary',
        secondary: 'border-secondary',
        tertiary: 'border-tertiary',
        info: 'border-info',
        success: 'border-success',
        warning: 'border-warning',
        danger: 'border-danger',
      },
      primary: {
        primary: 'bg-accent-primary-inverse',
        secondary: 'bg-secondary',
        tertiary: 'bg-tertiary',
        info: 'bg-accent-secondary-inverse',
        success: 'bg-success-inverse',
        warning: 'bg-warning-inverse text-warning',
        danger: 'bg-danger-inverse',
      },
    }

    const baseClasses =
      variant === 'outline'
        ? 'border hover:bg-interactive-background-neutral-hover focus:bg-interactive-background-neutral-hover active:bg-interactive-background-neutral-pressed'
        : 'text-primary-inverse hover:bg-interactive-background-success-hover-inverse focus:bg-interactive-background-success-hover-inverse active:bg-interactive-background-success-pressed-inverse disabled:bg-interactive-background-success-disabled-inverse'

    const colorClass = colorMap[variant][color] || colorMap[variant].primary
    const colorClasses = `${baseClasses} ${colorClass}`

    const sizeClasses =
      size === 'sm'
        ? 'px-2 py-1 leading-4 text-sm'
        : size === 'lg'
          ? 'px-6 py-3 leading-6 text-lg font-medium'
          : 'px-4 py-2 leading-5 text-base font-medium'

    const fontClasses = variant === 'outline' ? 'font-medium' : ''

    return (
      <Element
        ref={ref as any}
        type={trigger ? undefined : 'button'}
        onClick={onClick}
        disabled={disabled}
        className={`
        max-w-full justify-center gap-1
        flex flex-row items-center
        relative group/pc-icon-button
        rounded-md
        ${grow ? 'grow' : ''}
        ${sizeClasses}
        ${fontClasses} ${colorClasses}`}
        title={title}
        href={href}
        target={target}
      >
        {icon && <span>{icon}</span>}
        <div className="truncate">{children}</div>

        {tooltip && (
          <div role="tooltip" className="pc-icon-button__tooltip w-52 transition-opacity delay-500 opacity-0 z-30">
            <span className="pc-icon-button__tooltip__text whitespace-break-spaces rounded-lg">{tooltip}</span>
            <div className="pc-icon-button__tooltip__arrow"></div>
          </div>
        )}
      </Element>
    )
  }
)

Button.displayName = 'Button'
